import { ProcurementType } from '@/types';
import dayjs from 'dayjs';
import z from 'zod';

const optionalIdSchema = z.preprocess(
  (value) =>
    value === '' || value === null || value === undefined
      ? undefined
      : Number(value),
  z.number().int().positive().optional(),
);

const optionalDateSchema = z
  .string()
  .refine((value) => value === '' || dayjs(value).isValid(), {
    message: 'Select a valid date',
  })
  .optional();

const lineSchema = z
  .object({
    mode: z.enum(['manual', 'linked']),
    manual_part_number: z.string().optional(),
    description: z.string().optional(),
    part: optionalIdSchema,
    release: optionalIdSchema,
    qty: z.coerce.number().int().positive({
      message: 'Quantity must be a positive whole number',
    }),
    unit_price: z
      .string()
      .refine(
        (value) =>
          value === '' ||
          (Number.isFinite(Number(value)) && Number(value) >= 0),
        { message: 'Unit price must be zero or greater' },
      )
      .optional(),
    needed_by: optionalDateSchema,
    promised_date: optionalDateSchema,
    notes: z.string().optional(),
  })
  .superRefine((line, context) => {
    if (
      line.mode === 'manual' &&
      !String(line.manual_part_number ?? '').trim()
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Item / service is required',
        path: ['manual_part_number'],
      });
    }

    if (line.mode === 'linked' && !line.part) {
      context.addIssue({
        code: 'custom',
        message: 'Part is required',
        path: ['part'],
      });
    }
  });

export const directPurchaseOrderFormSchema = z.object({
  supplier: z.coerce.number().int().positive({
    message: 'Supplier is required',
  }),
  project_po_number: z.string().max(100).optional(),
  title: z.string().optional(),
  function_number: z.string().optional(),
  expected_delivery_date: optionalDateSchema,
  shipping_address: z.string().optional(),
  supplier_reference: z.string().optional(),
  lines: z.array(lineSchema).min(1, {
    message: 'Add at least one line',
  }),
});

export type DirectPurchaseOrderForm = z.infer<
  typeof directPurchaseOrderFormSchema
>;

export const createDefaultPurchaseOrderLine =
  (): DirectPurchaseOrderForm['lines'][number] => ({
    mode: 'manual',
    manual_part_number: '',
    description: '',
    part: undefined,
    release: undefined,
    qty: 1,
    unit_price: '',
    needed_by: '',
    promised_date: '',
    notes: '',
  });

const optionalText = (value?: string) => value?.trim() || null;
const optionalIsoDate = (value?: string) =>
  value && dayjs(value).isValid() ? dayjs(value).toISOString() : null;

export const buildDirectPurchaseOrderPayload = (
  formData: DirectPurchaseOrderForm,
  project: number,
): ProcurementType.PurchaseOrders.PostDirectPurchaseOrder['body'] => {
  const payload = {
    project,
    supplier: formData.supplier,
    project_po_number: optionalText(formData.project_po_number),
    title: optionalText(formData.title),
    function_number: optionalText(formData.function_number),
    expected_delivery_date: optionalIsoDate(formData.expected_delivery_date),
    shipping_address: optionalText(formData.shipping_address),
    supplier_reference: optionalText(formData.supplier_reference),
    lines: formData.lines.map((line) => {
      const common = {
        qty: line.qty,
        unit_price: optionalText(line.unit_price),
        needed_by: optionalIsoDate(line.needed_by),
        promised_date: optionalIsoDate(line.promised_date),
        notes: optionalText(line.notes),
      };

      if (line.mode === 'linked') {
        return {
          ...common,
          part: line.part!,
          ...(line.release ? { release: line.release } : {}),
        };
      }

      return {
        ...common,
        manual_part_number: line.manual_part_number!.trim(),
        description: optionalText(line.description),
      };
    }),
  };

  return ProcurementType.PurchaseOrders.postDirectPurchaseOrder.body.parse(
    payload,
  );
};
